<!-- Modal backdrop -->
<div
  class="fixed inset-0 z-[60] bg-black bg-opacity-45"
  (click)="onCancelClicked()"
>
  <div class="h-full flex items-start justify-center py-8 px-4">
    <div class="flex flex-row max-w-7xl w-full h-full">
      <!-- Main content area -->
      <div
        class="flex-1 flex flex-col bg-white dark:bg-gray-800 rounded-lg shadow-md relative z-[65]"
        (click)="$event.stopPropagation()"
        role="dialog"
        aria-labelledby="profile-title"
        aria-modal="true"
      >
        <div
          *ngIf="loading || isProfileDataLoading"
          class="absolute inset-0 z-[75] flex items-center justify-center bg-black bg-opacity-40 rounded-lg"
          role="status"
          aria-live="polite"
        >
          <div class="flex flex-col items-center gap-3 rounded-lg bg-white dark:bg-gray-800 px-6 py-5 shadow-lg border border-gray-200 dark:border-gray-700">
            <app-spinner class="w-10 h-10 text-blue-800" />
            <p class="text-sm font-medium text-gray-700 dark:text-gray-200">{{ 'Loading profile data...' | transloco }}</p>
          </div>
        </div>

        <div
          *ngIf="isChangingPassword && !(loading || isProfileDataLoading)"
          class="absolute inset-0 z-[75] flex items-center justify-center bg-black bg-opacity-40 rounded-lg"
          role="status"
          aria-live="polite"
        >
          <div class="flex flex-col items-center gap-3 rounded-lg bg-white dark:bg-gray-800 px-6 py-5 shadow-lg border border-gray-200 dark:border-gray-700">
            <app-spinner class="w-10 h-10 text-blue-800" />
            <p class="text-sm font-medium text-gray-700 dark:text-gray-200">{{ 'Changing your password...' | transloco }}</p>
          </div>
        </div>

        <!-- Header with title and close button -->
        <div
          class="p-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 rounded-t-lg flex items-center gap-4 relative"
          role="banner"
        >
          <div class="flex items-center flex-shrink-0">
            <h1 id="profile-title" class="text-gray-700 dark:text-gray-300 text-xl md:text-3xl font-semibold">{{ 'Edit Profile' | transloco }}</h1>
          </div>

          <!-- Vertical bar separator -->
          <div class="hidden md:flex w-0.5 bg-slate-300 dark:bg-slate-500 self-stretch ml-auto"></div>

          <!-- Close button -->
          <div class="hidden md:flex">
            <button
              type="button"
              class="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm w-8 h-8 flex items-center justify-center dark:hover:bg-gray-600 dark:hover:text-white"
              (click)="onCancelClicked()"
              [attr.aria-label]="'Close profile modal' | transloco"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
              <span class="sr-only">{{ 'Close profile modal' | transloco }}</span>
            </button>
          </div>
        </div>

        <!-- Top shadow -->
        <div
          class="pointer-events-none w-full h-2 z-20 -mb-2"
          style="background: linear-gradient(to bottom, rgba(0, 0, 0, 0.18), transparent);"
        ></div>

        <!-- Scrollable content area -->
        <div class="flex-1 flex flex-col min-h-0">
        <main class="flex-1 overflow-y-auto px-6 pb-4 pt-6" role="main">
          <form (ngSubmit)="onSubmit()" aria-labelledby="profile-title">
            <!-- Profile Header Section -->
            <div class="flex items-start gap-3 mb-8 pb-6 border-b border-gray-200 dark:border-gray-700">
              <!-- Profile Picture -->
              <div class="relative">
                <div class="w-24 h-24 rounded-full border-2 border-gray-200 overflow-hidden bg-gray-100 flex items-center justify-center">
                  <img
                    [src]="getProfilePictureUrl()"
                    [attr.alt]="user.firstName + ' ' + user.lastName"
                    class="w-full h-full object-cover"
                    (error)="onImageError($event)"
                  />
                  <!-- Default avatar fallback -->
                  <div *ngIf="!hasProfilePicture" class="text-gray-400 text-3xl font-bold">
                    {{ getInitials() }}
                  </div>
                </div>
                <!-- Upload button -->
                <button
                  type="button"
                  class="absolute bottom-0 right-0 w-8 h-8 bg-blue-800 text-white rounded-full flex items-center justify-center hover:bg-blue-900 transition duration-200 shadow-lg"
                  [attr.title]="'Change profile picture' | transloco"
                  [attr.aria-label]="'Change profile picture' | transloco"
                >
                  <ng-icon name="matEdit" size="16"></ng-icon>
                </button>
              </div>
              
              <!-- User Info -->
              <div class="flex-1">
                <h2 class="text-xl font-semibold text-gray-900 dark:text-white mb-1">
                  {{ user.firstName }} {{ user.lastName }}
                </h2>
                <p class="text-gray-600 dark:text-gray-300 mb-3">{{ user.email }}</p>
                <p class="text-sm text-gray-500 dark:text-gray-400">
                  {{ 'Update your profile information and account settings' | transloco }}
                </p>
              </div>
            </div>

            <!-- Form Fields Section -->
            <div class="space-y-6">
              <!-- Personal Information Section -->
              <div>
                <h2 class="w-full font-bold text-xl mb-3 border-b-2 pb-2">
                  {{ "Personal Information" | transloco }}
                </h2>
                
                <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <!-- First Name -->
                  <div>
                    <div class="mb-1 text-sm font-medium text-gray-700 dark:text-gray-100 border-b border-blue-300 pb-1 flex flex-row items-center justify-between">
                      <label for="firstName" class="flex flex-row items-center">
                        <span class="text-red-600 dark:text-red-500 mr-1">*</span>{{ 'First Name' | transloco }}
                      </label>
                    </div>
                    <input
                      id="firstName"
                      [(ngModel)]="user.firstName"
                      name="firstName"
                      type="text"
                      required
                      aria-required="true"
                      aria-describedby="firstName-hint"
                      class="w-full max-w-full flex-shrink-0 p-2 bg-gray-50 border-input border-blue-100 dark:border-gray-600 rounded-input placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 dark:bg-gray-700 dark:text-white transition duration-200"
                      [attr.placeholder]="'Enter your first name' | transloco"
                    />
                    <div id="firstName-hint" class="sr-only">{{ 'Enter your first name (required field)' | transloco }}</div>
                  </div>

                  <!-- Last Name -->
                  <div>
                    <div class="mb-1 text-sm font-medium text-gray-700 dark:text-gray-100 border-b border-blue-300 pb-1 flex flex-row items-center justify-between">
                      <label for="lastName" class="flex flex-row items-center">
                        <span class="text-red-600 dark:text-red-500 mr-1">*</span>{{ 'Last Name' | transloco }}
                      </label>
                    </div>
                    <input
                      id="lastName"
                      [(ngModel)]="user.lastName"
                      name="lastName"
                      type="text"
                      required
                      aria-required="true"
                      aria-describedby="lastName-hint"
                      class="w-full max-w-full flex-shrink-0 p-2 bg-gray-50 border-input border-blue-100 dark:border-gray-600 rounded-input placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 dark:bg-gray-700 dark:text-white transition duration-200"
                      [attr.placeholder]="'Enter your last name' | transloco"
                    />
                    <div id="lastName-hint" class="sr-only">{{ 'Enter your last name (required field)' | transloco }}</div>
                  </div>
                </div>
              </div>

              <!-- Contact Information Section -->
              <div>
                <h2 class="w-full font-bold text-xl mb-3 border-b-2 pb-2">
                  {{ "Contact Information" | transloco }}
                </h2>
                
                <div class="space-y-3">
                <!-- Email Field -->
                <div>
                  <div class="mb-1 text-sm font-medium text-gray-700 dark:text-gray-100 border-b border-blue-300 pb-1 flex flex-row items-center justify-between">
                    <label for="email" class="flex flex-row items-center">
                      <span class="text-red-600 dark:text-red-500 mr-1">*</span>{{ 'Email Address' | transloco }}
                    </label>
                  </div>
                  <input
                    id="email"
                    [(ngModel)]="user.email"
                    name="email"
                    type="email"
                    required
                    aria-required="true"
                    aria-describedby="email-hint"
                    disabled
                    class="w-full max-w-full flex-shrink-0 p-2 bg-gray-100 border-input border-blue-100 dark:border-gray-600 rounded-input placeholder-gray-400 dark:placeholder-gray-500 dark:bg-gray-800 dark:text-white transition duration-200 cursor-not-allowed"
                    [attr.placeholder]="'Email address' | transloco"
                  />
                  <p id="email-hint" class="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {{ 'Email address cannot be edited.' | transloco }}
                  </p>
                </div>

                <!-- Phone Number -->
                <div>
                  <div class="mb-1 text-sm font-medium text-gray-700 dark:text-gray-100 border-b border-blue-300 pb-1 flex flex-row items-center justify-between">
                    <label for="phone" class="flex flex-row items-center">
                      {{ 'Phone Number' | transloco }}
                    </label>
                  </div>
                  <input
                    id="phone"
                    [(ngModel)]="user.phone"
                    name="phone"
                    type="tel"
                    aria-describedby="phone-hint"
                    class="w-full max-w-full flex-shrink-0 p-2 bg-gray-50 border-input border-blue-100 dark:border-gray-600 rounded-input placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 dark:bg-gray-700 dark:text-white transition duration-200"
                    [attr.placeholder]="'Enter your phone number' | transloco"
                  />
                  <div id="phone-hint" class="sr-only">{{ 'Enter your phone number (optional field)' | transloco }}</div>
                </div>
                </div>
              </div>

              <!-- Address Section -->
              <div>
                <h2 class="w-full font-bold text-xl mb-3 border-b-2 pb-2">
                  {{ "Address Information" | transloco }}
                </h2>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <div class="mb-1 text-sm font-medium text-gray-700 dark:text-gray-100 border-b border-blue-300 pb-1 flex flex-row items-center justify-between">
                      <label for="street1" class="flex flex-row items-center">
                        {{ 'Street 1' | transloco }}
                      </label>
                    </div>
                    <input
                      id="street1"
                      [(ngModel)]="user.address!.street1"
                      name="street1"
                      type="text"
                      class="w-full max-w-full flex-shrink-0 p-2 bg-gray-50 border-input border-blue-100 dark:border-gray-600 rounded-input placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 dark:bg-gray-700 dark:text-white transition duration-200"
                      [attr.placeholder]="'Enter street address' | transloco"
                    />
                  </div>

                  <div>
                    <div class="mb-1 text-sm font-medium text-gray-700 dark:text-gray-100 border-b border-blue-300 pb-1 flex flex-row items-center justify-between">
                      <label for="street2" class="flex flex-row items-center">
                        {{ 'Street 2' | transloco }}
                      </label>
                    </div>
                    <input
                      id="street2"
                      [(ngModel)]="user.address!.street2"
                      name="street2"
                      type="text"
                      class="w-full max-w-full flex-shrink-0 p-2 bg-gray-50 border-input border-blue-100 dark:border-gray-600 rounded-input placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 dark:bg-gray-700 dark:text-white transition duration-200"
                      [attr.placeholder]="'Enter additional address' | transloco"
                    />
                  </div>

                  <div>
                    <div class="mb-1 text-sm font-medium text-gray-700 dark:text-gray-100 border-b border-blue-300 pb-1 flex flex-row items-center justify-between">
                      <label for="city" class="flex flex-row items-center">
                        {{ 'City' | transloco }}
                      </label>
                    </div>
                    <input
                      id="city"
                      [(ngModel)]="user.address!.city"
                      name="city"
                      type="text"
                      class="w-full max-w-full flex-shrink-0 p-2 bg-gray-50 border-input border-blue-100 dark:border-gray-600 rounded-input placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 dark:bg-gray-700 dark:text-white transition duration-200"
                      [attr.placeholder]="'Enter city' | transloco"
                    />
                  </div>

                  <div>
                    <div class="mb-1 text-sm font-medium text-gray-700 dark:text-gray-100 border-b border-blue-300 pb-1 flex flex-row items-center justify-between">
                      <label for="postalCode" class="flex flex-row items-center">
                        {{ getPostalCodeLabel() | transloco }}
                      </label>
                    </div>
                    <input
                      id="postalCode"
                      [(ngModel)]="user.address!.postalCode"
                      name="postalCode"
                      type="text"
                      [disabled]="!user.address?.countryId"
                      class="w-full max-w-full flex-shrink-0 p-2 bg-gray-50 border-input border-blue-100 dark:border-gray-600 rounded-input placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 dark:bg-gray-700 dark:text-white transition duration-200 disabled:bg-gray-100 disabled:cursor-not-allowed dark:disabled:bg-gray-800"
                      [attr.placeholder]="'Enter postal code' | transloco"
                    />
                  </div>

                  <div>
                    <div class="mb-1 text-sm font-medium text-gray-700 dark:text-gray-100 border-b border-blue-300 pb-1 flex flex-row items-center justify-between">
                      <label for="countryId" class="flex flex-row items-center">
                        <span class="text-red-600 dark:text-red-500 mr-1">*</span>{{ 'Country' | transloco }}
                      </label>
                    </div>
                    <select
                      id="countryId"
                      [(ngModel)]="user.address!.countryId"
                      name="countryId"
                      (ngModelChange)="onCountryChange($event)"
                      class="w-full max-w-full p-2 bg-gray-50 border-input border-blue-100 dark:border-gray-600 rounded-input focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 dark:bg-gray-700 dark:text-white transition duration-200"
                    >
                      <option [ngValue]="0">{{ 'Select Country' | transloco }}</option>
                      <option *ngFor="let country of countries" [ngValue]="country.id">{{ country.name }}</option>
                    </select>
                  </div>

                  <div>
                    <div class="mb-1 text-sm font-medium text-gray-700 dark:text-gray-100 border-b border-blue-300 pb-1 flex flex-row items-center justify-between">
                      <label for="districtId" class="flex flex-row items-center">
                        <span class="text-red-600 dark:text-red-500 mr-1">*</span>{{ getDistrictLabel() | transloco }}
                      </label>
                    </div>
                    <select
                      id="districtId"
                      [(ngModel)]="user.address!.districtId"
                      name="districtId"
                      [disabled]="!user.address?.countryId"
                      (ngModelChange)="onDistrictChange($event)"
                      class="w-full max-w-full p-2 bg-gray-50 border-input border-blue-100 dark:border-gray-600 rounded-input focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 dark:bg-gray-700 dark:text-white transition duration-200 disabled:bg-gray-100 disabled:cursor-not-allowed dark:disabled:bg-gray-800"
                    >
                      <option [ngValue]="0">{{ 'Select' | transloco }} {{ getDistrictLabel() }}</option>
                      <option *ngFor="let district of districts" [ngValue]="district.id">{{ district.name }}</option>
                    </select>
                  </div>
                </div>

              </div>

              <!-- Security Section -->
              <div>
                <h2 class="w-full font-bold text-xl mb-3 border-b-2 pb-2">
                  {{ "Security Settings" | transloco }}
                </h2>
                
                <!-- Change Password Button (hidden when fields are visible) -->
                <button
                  *ngIf="!showPasswordFields"
                  type="button"
                  (click)="showPasswordFields = true"
                  class="w-full md:w-auto px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition duration-200 flex items-center justify-center gap-2 border border-gray-300 dark:border-gray-600"
                >
                  <ng-icon name="matEdit" size="18"></ng-icon>
                  {{ 'Change Password' | transloco }}
                </button>

                <!-- Password Fields -->
                <div *ngIf="showPasswordFields" class="space-y-3">
                  <!-- Old Password -->
                  <div>
                    <div class="mb-1 text-sm font-medium text-gray-700 dark:text-gray-100 border-b border-blue-300 pb-1 flex flex-row items-center justify-between">
                      <label for="oldPassword" class="flex flex-row items-center">
                        <span class="text-red-600 dark:text-red-500 mr-1">*</span>{{ 'Current Password' | transloco }}
                      </label>
                    </div>
                    <input
                      id="oldPassword"
                      [(ngModel)]="userPassword.oldPassword"
                      name="oldPassword"
                      type="password"
                      required
                      [disabled]="isChangingPassword"
                      aria-required="true"
                      aria-describedby="oldPassword-hint"
                      class="w-full max-w-full flex-shrink-0 p-2 bg-gray-50 border-input border-blue-100 dark:border-gray-600 rounded-input placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 dark:bg-gray-700 dark:text-white transition duration-200"
                      [attr.placeholder]="'Enter your current password' | transloco"
                    />
                    <div id="oldPassword-hint" class="sr-only">{{ 'Enter your current password to verify identity' | transloco }}</div>
                  </div>

                  <!-- New Password -->
                  <div>
                    <div class="mb-1 text-sm font-medium text-gray-700 dark:text-gray-100 border-b border-blue-300 pb-1 flex flex-row items-center justify-between">
                      <label for="newPassword" class="flex flex-row items-center">
                        <span class="text-red-600 dark:text-red-500 mr-1">*</span>{{ 'New Password' | transloco }}
                      </label>
                    </div>
                    <input
                      id="newPassword"
                      [(ngModel)]="userPassword.newPassword"
                      name="newPassword"
                      type="password"
                      required
                      [disabled]="isChangingPassword"
                      aria-required="true"
                      aria-describedby="newPassword-hint"
                      class="w-full max-w-full flex-shrink-0 p-2 bg-gray-50 border-input border-blue-100 dark:border-gray-600 rounded-input placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 dark:bg-gray-700 dark:text-white transition duration-200"
                      [attr.placeholder]="'Enter your new password' | transloco"
                    />
                    <div id="newPassword-hint" class="sr-only">{{ 'Enter a new secure password' | transloco }}</div>
                  </div>

                  <!-- Confirm New Password -->
                  <div>
                    <div class="mb-1 text-sm font-medium text-gray-700 dark:text-gray-100 border-b border-blue-300 pb-1 flex flex-row items-center justify-between">
                      <label for="confirmPassword" class="flex flex-row items-center">
                        <span class="text-red-600 dark:text-red-500 mr-1">*</span>{{ 'Confirm New Password' | transloco }}
                      </label>
                    </div>
                    <input
                      id="confirmPassword"
                      [(ngModel)]="userPassword.confirmPassword"
                      name="confirmPassword"
                      type="password"
                      required
                      [disabled]="isChangingPassword"
                      aria-required="true"
                      aria-describedby="confirmPassword-hint confirmPassword-error"
                      class="w-full max-w-full flex-shrink-0 p-2 bg-gray-50 border-input rounded-input placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 dark:bg-gray-700 dark:text-white transition duration-200"
                      [class.border-red-500]="userPassword.newPassword !== userPassword.confirmPassword && userPassword.confirmPassword"
                      [class.border-blue-300]="userPassword.newPassword === userPassword.confirmPassword || !userPassword.confirmPassword"
                      [class.dark:border-red-500]="userPassword.newPassword !== userPassword.confirmPassword && userPassword.confirmPassword"
                      [class.dark:border-gray-600]="userPassword.newPassword === userPassword.confirmPassword || !userPassword.confirmPassword"
                      [attr.placeholder]="'Confirm your new password' | transloco"
                      (input)="checkPasswordMatch()"
                    />
                    <div id="confirmPassword-hint" class="sr-only">{{ 'Re-enter your new password to confirm it matches' | transloco }}</div>
                    <!-- Warning Message -->
                    <p *ngIf="userPassword.newPassword !== userPassword.confirmPassword && userPassword.confirmPassword"
                       id="confirmPassword-error"
                       class="text-red-500 text-sm mt-1 flex items-center gap-1"
                       role="alert"
                       aria-live="polite">
                      <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd"></path>
                      </svg>
                      {{ 'Passwords do not match' | transloco }}
                    </p>
                  </div>

                  <!-- Password Action Buttons -->
                  <div class="flex flex-col sm:flex-row gap-3 pt-2">
                    <button
                      type="button"
                      (click)="changePassword()"
                      [disabled]="isChangingPassword || userPassword.newPassword !== userPassword.confirmPassword || !userPassword.oldPassword || !userPassword.newPassword"
                      class="px-4 py-2 bg-blue-800 text-white rounded-lg hover:bg-blue-900 disabled:bg-gray-300 disabled:cursor-not-allowed transition duration-200 flex items-center justify-center gap-2"
                    >
                      <ng-container *ngIf="isChangingPassword; else updatePasswordLabel">
                        <span class="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                        {{ 'Updating...' | transloco }}
                      </ng-container>
                      <ng-template #updatePasswordLabel>
                        <ng-icon name="matEdit" size="16"></ng-icon>
                        {{ 'Update Password' | transloco }}
                      </ng-template>
                    </button>
                    <button
                      type="button"
                      (click)="cancelPasswordChange()"
                      [disabled]="isChangingPassword"
                      class="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition duration-200 flex items-center justify-center gap-2 border border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-400"
                    >
                      <ng-icon name="matCloseOutline" size="16"></ng-icon>
                      {{ 'Cancel' | transloco }}
                    </button>
                  </div>

                </div>
              </div>
            </div>
          </form>
        </main>
        </div>

        <!-- Bottom shadow: placed just before the footer, outside the scrollable area -->
        <div class="pointer-events-none w-full h-2 z-20 -mt-2"
             style="background: linear-gradient(to top, rgba(0,0,0,0.18), transparent);"></div>

        <!-- Footer with buttons -->
        <footer class="bg-gray-100 dark:bg-gray-800 flex flex-col items-center justify-center p-4 border-t rounded-b dark:border-gray-600 shadow-lg z-10 relative max-sm:rounded-none gap-2" role="contentinfo">
          <!-- Save error message -->
          <div *ngIf="saveError" class="w-full flex items-center gap-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 rounded px-3 py-2 text-sm">
            <ng-icon name="matErrorOutline" size="16"></ng-icon>
            <span>{{ saveError }}</span>
          </div>
          <div class="flex flex-row gap-2">
            <!-- Save button -->
            <button
              type="button"
              (click)="onSubmit()"
              class="bg-blue-800 hover:bg-blue-900 text-white text-base font-medium py-2 px-4 rounded transition-colors duration-200 min-w-[120px]"
            >
              {{ 'Save' | transloco }}
            </button>

            <!-- Cancel button -->
            <button
              type="button"
              (click)="onCancelClicked()"
              class="bg-gray-600 hover:bg-gray-700 text-white text-base font-medium py-2 px-4 rounded transition-colors duration-200 min-w-[120px] focus:outline-none focus:ring-2 focus:ring-gray-400"
            >
              {{ 'Cancel' | transloco }}
            </button>
          </div>
        </footer>
      </div>
    </div>
  </div>
</div>

<!-- SUCCESS MESSAGE POPUP -->
<div
  *ngIf="showSuccessMessage"
  class="fixed inset-0 z-[70] flex items-center justify-center bg-black bg-opacity-60 transition-opacity duration-300"
  (click)="closeSuccessMessage()"
>
  <div
    class="bg-white dark:bg-gray-800 rounded-lg shadow-lg max-w-md w-full mx-4 transform transition-transform duration-300"
    (click)="$event.stopPropagation()"
  >
    <div class="flex flex-col items-center justify-center p-8">
      <div class="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-6">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="h-8 w-8 text-green-600"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M5 13l4 4L19 7"
          />
        </svg>
      </div>
      <h3 class="w-full text-center text-xl font-medium text-gray-900 dark:text-white mb-3">
        {{ 'Profile Saved!' | transloco }}
      </h3>
      <p class="w-full max-w-prose text-sm text-gray-700 dark:text-gray-100 text-center mb-6">
        {{ 'Your profile has been updated successfully.' | transloco }}
      </p>
      <button
        (click)="closeSuccessMessage()"
        class="bg-green-600 hover:bg-green-700 text-white text-sm font-medium py-2 px-6 rounded transition-colors duration-200"
        [attr.aria-label]="'Close success message' | transloco"
      >
        {{ 'Continue' | transloco }}
      </button>
    </div>
  </div>
</div>

<!-- PASSWORD CHANGE SUCCESS MODAL -->
<div
  *ngIf="showPasswordChangeSuccess"
  class="fixed inset-0 z-[70] flex items-center justify-center bg-black bg-opacity-60 transition-opacity duration-300"
>
  <div class="bg-white dark:bg-gray-800 rounded-lg shadow-lg max-w-md w-full mx-4 transform transition-transform duration-300">
    <div class="flex flex-col items-center justify-center p-8">
      <div class="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-6">
        <svg
          class="w-8 h-8 text-green-600"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      </div>

      <h3 class="text-xl font-medium text-gray-900 dark:text-white mb-3">
        {{ "Success!" | transloco }}
      </h3>

      <p class="text-sm text-gray-700 dark:text-gray-100 text-center mb-6">
        {{ passwordChangeMessage }}
      </p>

      <button
        (click)="closePasswordChangeSuccess()"
        type="button"
        class="bg-green-600 hover:bg-green-700 text-white text-sm font-medium py-2 px-6 rounded transition-colors duration-200"
      >
        {{ "Continue" | transloco }}
      </button>
    </div>
  </div>
</div>

<!-- PASSWORD CHANGE ERROR MODAL -->
<div
  *ngIf="showPasswordChangeError"
  class="fixed inset-0 z-[70] flex items-center justify-center bg-black bg-opacity-60 transition-opacity duration-300"
>
  <div class="bg-white dark:bg-gray-800 rounded-lg shadow-lg max-w-md w-full mx-4 transform transition-transform duration-300">
    <div class="flex flex-col items-center justify-center p-8">
      <div class="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-6">
        <svg
          class="w-8 h-8 text-red-600"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </div>

      <h3 class="text-xl font-medium text-gray-900 dark:text-white mb-3">
        {{ "Error!" | transloco }}
      </h3>

      <div class="mb-6 max-w-md w-full">
        <div class="bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg p-4 max-h-40 overflow-y-auto overflow-x-hidden text-left">
          <p class="text-sm text-gray-700 dark:text-gray-100">
            {{ passwordChangeError }}
          </p>
        </div>
      </div>

      <button
        (click)="closePasswordChangeError()"
        type="button"
        class="bg-gray-600 hover:bg-gray-700 text-white text-sm font-medium py-2 px-6 rounded transition-colors duration-200"
      >
        {{ "Close" | transloco }}
      </button>
    </div>
  </div>
</div>

<!-- PROFILE LOAD ERROR MODAL -->
<div
  *ngIf="showProfileLoadError"
  class="fixed inset-0 z-[70] flex items-center justify-center bg-black bg-opacity-60 transition-opacity duration-300"
>
  <div class="bg-white dark:bg-gray-800 rounded-lg shadow-lg max-w-md w-full mx-4 transform transition-transform duration-300">
    <div class="flex flex-col items-center justify-center p-8">
      <div class="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-6">
        <svg
          class="w-8 h-8 text-red-600"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </div>

      <h3 class="text-xl font-medium text-gray-900 dark:text-white mb-3">
        {{ "Error!" | transloco }}
      </h3>

      <div class="mb-6 max-w-md w-full">
        <div class="bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg p-4 max-h-40 overflow-y-auto overflow-x-hidden text-left">
          <p class="text-sm text-gray-700 dark:text-gray-100">
            {{ profileLoadErrorMessage }}
          </p>
        </div>
      </div>

      <button
        (click)="closeProfileLoadError()"
        type="button"
        class="bg-gray-600 hover:bg-gray-700 text-white text-sm font-medium py-2 px-6 rounded transition-colors duration-200"
      >
        {{ "Close" | transloco }}
      </button>
    </div>
  </div>
</div>

<!-- CANCEL CONFIRMATION POPUP -->
<div
  *ngIf="showCancelConfirmPopup"
  class="fixed inset-0 z-[70] flex items-center justify-center bg-black bg-opacity-60 transition-opacity duration-300"
  [class.opacity-0]="isCancelPopupFading"
  [class.opacity-100]="!isCancelPopupFading"
>
  <div class="bg-white dark:bg-gray-800 rounded-lg shadow-lg max-w-md w-full mx-4 transform transition-transform duration-300"
       [class.scale-95]="isCancelPopupFading"
       [class.scale-100]="!isCancelPopupFading">
    
    <!-- Content area -->
    <div class="flex flex-col items-center justify-center p-8">
      <div class="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-yellow-100 mb-6">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="h-10 w-10 text-yellow-600"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M13 16h-1v-4h-1m1-4h.01M12 20a8 8 0 100-16 8 8 0 000 16z"
          />
        </svg>
      </div>
      <h3 class="text-xl font-medium text-gray-900 dark:text-white mb-3">
        {{ 'Discard Unsaved Changes?' | transloco }}
      </h3>
      <p class="text-sm text-gray-700 dark:text-gray-100 text-center mb-6">
        {{ 'You have unsaved changes that will be permanently lost if you close this form. Are you sure you want to discard your changes?' | transloco }}
      </p>

      <!-- Buttons -->
      <div class="flex w-full flex-col gap-3 sm:flex-row sm:justify-center">
        <button
          (click)="confirmCancel()"
          class="w-full sm:w-auto bg-red-600 hover:bg-red-700 text-white text-sm font-medium py-2 px-6 rounded text-center transition-colors duration-200"
        >
          {{ 'Discard Changes' | transloco }}
        </button>
        <button
          (click)="dismissCancelPopup()"
          class="w-full sm:w-auto bg-blue-800 hover:bg-blue-900 text-white text-sm font-medium py-2 px-6 rounded text-center transition-colors duration-200"
        >
          {{ 'Keep Editing' | transloco }}
        </button>
      </div>
    </div>
  </div>
</div>
